"use client";

import { useState } from "react";
import { Box, Grid, Paper, Typography, TextField, Alert } from "@mui/material";
import { Row } from "simple-flexbox";
import { useSelector } from "react-redux";
import LockIcon from "@mui/icons-material/Lock";

import { GameTable } from "../transaction/table.js";
import SelectTextFields from "../../components/customDropDown/dropDown";
import CustomizedButtons from "../../components/button/button";

import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { transcationArrayType, transcationMode, walletFilterOptions, ACCESS_LEVEL } from "../../constant/constant";
import { parseGameTranscation } from "../../helper";
import jwt_decode from "jwt-decode";

const defaultDateTimeState = { date_from: "", date_to: "" };
const defaultTransactionType = { value: "All", label: "All", _id: 0 };

export default function MemberManagerPage() {
  const token = useSelector((s) => s.loginSlice.token);
  let isCashier = false;
  try { isCashier = token ? jwt_decode(token)?.accessLevel === ACCESS_LEVEL.CASHIER : false; } catch { isCashier = false; }

  const [unlocked, setUnlocked] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpErr, setOtpErr] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [dateTime, setDateTime] = useState(defaultDateTimeState);
  const [totalAmt, setTotalAmt] = useState(0);
  const [totalCashbackAmt, setTotalCashbackAmt] = useState(0);
  const [gameTranscation, setGameTranscation] = useState([]);
  const [transactionType, setTransactionType] = useState(defaultTransactionType);
  const [transactionMode, setTransactionMode] = useState(defaultTransactionType);
  const [walletFilter, setWalletFilter] = useState(defaultTransactionType);
  const [transcation, setTranscation] = useState({});
  const [disableOtherFilters, setDisableOtherFilters] = useState(false);
  const [phoneOrPlayerId, setPhoneOrPlayerId] = useState("");
  const [offerApprovalFilter, setOfferApprovalFilter] = useState({ value: "All", _id: 0 });
  const [cashbackFilter, setCashbackFilter] = useState({ value: "All", _id: 0 });

  const offerApprovalOptions = [
    { value: "All", label: "All", _id: 0 },
    { value: "PENDING", label: "Pending Approval", _id: 1 },
    { value: "APPROVED", label: "Approved", _id: 2 },
    { value: "REJECTED", label: "Rejected", _id: 3 },
    { value: "AUTO", label: "Auto (No Approval)", _id: 4 },
  ];

  const [pagination, setPagination] = useState({ currentPage: 1, totalPage: 1, size: 20 });
  const [searched, setSearched] = useState(false);

  const requestOtp = async () => {
    setRequesting(true);
    setOtpErr("");
    try {
      const res = await axiosRequest("POST", `${apiBaseUrl}/member/request-otp`, token, {});
      if (res?.data?.status === false) { setOtpErr(res?.data?.message || "Could not send OTP"); return; }
      setOtpSent(true);
    } finally {
      setRequesting(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp.trim()) return;
    setVerifying(true);
    setOtpErr("");
    try {
      const res = await axiosRequest("POST", `${apiBaseUrl}/member/verify-otp`, token, { otp: otp.trim() });
      if (!res?.data?.status) { setOtpErr(res?.data?.message || "Invalid OTP"); return; }
      setUnlocked(true);
      setOtp("");
      getMemberTranscation();
    } finally {
      setVerifying(false);
    }
  };

  const getMemberTranscation = async (page = 1, size = 20, refreshData) => {
    let postData = await parseGameTranscation(transcation, transactionType, dateTime, page, size, transactionMode, walletFilter);
    if (offerApprovalFilter?.value && offerApprovalFilter.value !== "All") {
      postData.offer_approval_status = offerApprovalFilter.value;
    }
    if (cashbackFilter?.value === "HAS_CASHBACK") {
      postData.has_cashback = true;
    }
    if (refreshData) postData = refreshData;

    const res = await axiosRequest("POST", `${apiBaseUrl}/member/transactions`, token, postData);

    if (res?.data?.otp_required) {
      // the 15-minute access window expired mid-session -- drop back to the OTP gate
      setUnlocked(false);
      setOtpSent(false);
      return;
    }
    if (res?.data?.status) {
      setGameTranscation(res.data.transactionList);
      setPagination(res.data.pagniationData);
      setTotalAmt(res.data.totalTnxAmt || 0);
      setTotalCashbackAmt(res.data.totalCashbackAmt || 0);
      setSearched(true);
    }
  };

  const handleSubmit = () => { getMemberTranscation(); };

  const handleReset = () => {
    setDateTime(defaultDateTimeState);
    setTranscation({});
    setTransactionType(defaultTransactionType);
    setTransactionMode(defaultTransactionType);
    setWalletFilter(defaultTransactionType);
    setOfferApprovalFilter({ value: "All", _id: 0 });
    setCashbackFilter({ value: "All", _id: 0 });
    setDisableOtherFilters(false);
    setSearched(false);
    setTotalAmt(0);
    setTotalCashbackAmt(0);
    setPhoneOrPlayerId("");
    getMemberTranscation(1, 20, defaultDateTimeState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTranscation((prev) => {
      const updated = { ...prev };
      delete updated.phone;
      delete updated.player_id;

      if (name === "phone_or_player_id") {
        setPhoneOrPlayerId(value);
        const trimmed = value.trim();
        if (trimmed) {
          if (/^\d{10}$/.test(trimmed)) updated.phone = trimmed;
          else updated.player_id = trimmed;
        }
      } else {
        if (value.trim()) updated[name] = value.trim();
        else delete updated[name];
      }

      setDisableOtherFilters(Object.keys(updated).length > 0);
      return updated;
    });
  };

  const setDate = (date, key) => setDateTime((prev) => ({ ...prev, [key]: date }));
  const handleTransactionType = (label, value, _id = 1) => setTransactionType({ value, _id });
  const handleTransactionMode = (label, value, _id = 1) => setTransactionMode({ value, _id });

  if (!unlocked) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8, maxWidth: 480, mx: "auto" }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid #e0e0e0", textAlign: "center" }}>
          <LockIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
          <Typography variant="h5" fontWeight={700} mb={3}>Member Manager</Typography>

          {!otpSent ? (
            <CustomizedButtons color="success" label={requesting ? "Sending…" : "Request OTP"} variant="contained" onClick={requestOtp} disabled={requesting} />
          ) : (
            <>
              <TextField
                fullWidth size="small" label="Enter OTP" value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputMode="numeric" sx={{ mb: 2 }} autoFocus
              />
              <Box display="flex" gap={1.5} justifyContent="center">
                <CustomizedButtons color="error" label="Resend" variant="contained" onClick={requestOtp} disabled={requesting} />
                <CustomizedButtons color="success" label={verifying ? "Checking…" : "Verify"} variant="contained" onClick={verifyOtp} disabled={verifying || !otp.trim()} />
              </Box>
            </>
          )}
          {otpErr && <Alert severity="error" sx={{ mt: 2 }}>{otpErr}</Alert>}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", marginTop: { xs: "55px", md: "95px" }, pb: 6 }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mb={3}>
          <Typography variant="h5">Member Manager</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item lg={3} xs={12}>
            <TextField
              size="small" fullWidth label="Transaction Id" name="transaction_id"
              value={transcation?.transaction_id || ""} onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.transaction_id}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small" fullWidth label="Phone / Player ID" name="phone_or_player_id"
              value={phoneOrPlayerId} onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.phone && !transcation?.player_id}
              helperText="10-digit number = phone; otherwise player ID"
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small" fullWidth label="Player Name" name="name"
              value={transcation?.name || ""} onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.name}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small" fullWidth label="User Email" name="user_email"
              value={transcation?.user_email || ""} onChange={handleChange}
              disabled={disableOtherFilters && !transcation?.user_email}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields label="Transaction Type" value={transactionType?.value} handleOnChange={handleTransactionType} list={transcationArrayType} />
          </Grid>
          <Grid item lg={3} xs={12}>
            <SelectTextFields label="Transaction Mode" value={transactionMode?.value} handleOnChange={handleTransactionMode} list={transcationMode} />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields label="Offer Approval Status" value={offerApprovalFilter?.value} handleOnChange={(label, value, _id) => setOfferApprovalFilter({ value, _id })} list={offerApprovalOptions} />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields
              label="Deposit Offer Cashback" value={cashbackFilter?.value}
              handleOnChange={(label, value, _id) => setCashbackFilter({ value, _id })}
              list={[
                { value: "All", label: "All", _id: 0 },
                { value: "HAS_CASHBACK", label: "Has Cashback Only", _id: 1 },
              ]}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <SelectTextFields label="Wallet Deducted" value={walletFilter?.value} handleOnChange={(label, value, _id) => setWalletFilter({ value, _id })} list={walletFilterOptions} />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small" fullWidth label="Date & Time From" type="datetime-local"
              value={dateTime?.date_from || ""} onChange={(e) => setDate(e.target.value, "date_from")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item lg={3} xs={12}>
            <TextField
              size="small" fullWidth label="Date & Time To" type="datetime-local"
              value={dateTime?.date_to || ""} onChange={(e) => setDate(e.target.value, "date_to")}
              InputLabelProps={{ shrink: true }}
              disabled={!dateTime?.date_from}
              inputProps={{ min: dateTime?.date_from || "" }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <CustomizedButtons color="success" label="SEARCH" variant="contained" onClick={handleSubmit} />
              <CustomizedButtons color="error" label="RESET" variant="contained" onClick={handleReset} />
            </Box>
          </Grid>

          {searched && !isCashier && (
            <Grid item xs={12}>
              <Box display="flex" gap={3} alignItems="center" flexWrap="wrap"
                sx={{ p: 2, borderRadius: 2, background: "linear-gradient(135deg,#1e3c72,#2a5298)", color: "#fff" }}>
                <Box>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Transactions</Typography>
                  <Typography variant="h6" fontWeight={700}>{pagination.totalRecords ?? gameTranscation.length}</Typography>
                </Box>
                <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Amount</Typography>
                  <Typography variant="h5" fontWeight={800}>{Number(totalAmt || 0).toLocaleString("en-IN")}</Typography>
                </Box>
                {totalCashbackAmt > 0 && (
                  <Box sx={{ borderLeft: "1px solid rgba(255,255,255,0.3)", pl: 3 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Cashback</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: "#ffd54f" }}>{Number(totalCashbackAmt).toLocaleString("en-IN")}</Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {searched && gameTranscation?.length === 0 ? (
        <Paper sx={{ mt: 3, p: 6, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">No transactions found</Typography>
          <Typography variant="body2" color="text.disabled" mt={1}>Try adjusting your filters and search again.</Typography>
        </Paper>
      ) : gameTranscation?.length > 0 ? (
        <Paper sx={{ mt: 3 }}>
          <Row justifyContent="center" style={{ width: "100%" }}>
            <GameTable
              gameTranscation={gameTranscation}
              pagination={pagination}
              getGameTranscation={getMemberTranscation}
            />
          </Row>
        </Paper>
      ) : null}
    </Box>
  );
}
